import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  chartAxisTick,
  chartBarCursor,
  chartLegendStyle,
  chartLineCursor,
  chartTooltipProps,
} from '@/shared/lib/chart-theme';
import { brl, decimal } from '@/shared/lib/format';
import type { CampaignReport } from '../api/use-campaign-report';

const HEIGHT = 240;
const GRID = 'stroke-border/40';

/** Eixo em R$ curto ("R$ 600", "R$ 1,2 mil"): o valor cheio fica no tooltip. */
const brlAxis = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const axisMoney = (v: number) => brlAxis.format(v);
const axisCount = (v: number) => decimal(v);

/** Topo redondo do eixo (560 → 600, 950 → 1.000), como o Recharts arredonda o último tick. */
function niceCeil(n: number): number {
  if (!(n > 0)) return 0;
  const step = 10 ** Math.floor(Math.log10(n));
  return Math.ceil(n / step) * step;
}

/**
 * Largura do eixo Y para o maior rótulo caber. O Recharts não mede o texto: com
 * largura fixa, "R$ 600,00" perdia o "R" no celular.
 */
function yAxisWidth(max: number, format: (v: number) => string): number {
  const longest = Math.max(format(max).length, format(niceCeil(max)).length);
  return Math.max(32, Math.ceil(longest * 7.5) + 12);
}

/** "2026-10-24" → "24/10". */
function shortDay(key: string): string {
  const [, month, day] = key.split('-');
  return `${day}/${month}`;
}

function Empty({ children }: { children: string }) {
  return (
    <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height: HEIGHT }}>
      {children}
    </div>
  );
}

/** Entregas por dia: entregues × falhas. */
export function DispatchesChart({ daily }: { daily: CampaignReport['daily'] }) {
  const data = daily
    .filter((d) => d.delivered + d.failed > 0)
    .map((d) => ({ label: shortDay(d.date), Entregues: d.delivered, Falhas: d.failed }));
  if (data.length === 0) return <Empty>Nenhum disparo no período.</Empty>;
  const max = Math.max(...data.map((d) => d.Entregues + d.Falhas));

  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className={GRID} vertical={false} />
        <XAxis dataKey="label" tick={chartAxisTick} tickLine={false} axisLine={false} />
        <YAxis
          tick={chartAxisTick}
          tickLine={false}
          axisLine={false}
          width={yAxisWidth(max, axisCount)}
          tickFormatter={axisCount}
        />
        <Tooltip formatter={(v: number) => decimal(v)} cursor={chartBarCursor} {...chartTooltipProps} />
        <Legend wrapperStyle={chartLegendStyle} />
        <Bar dataKey="Entregues" stackId="a" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
        <Bar dataKey="Falhas" stackId="a" fill="hsl(var(--destructive))" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Receita atribuída por dia (pedidos no tooltip). */
export function RevenueChart({ daily }: { daily: CampaignReport['daily'] }) {
  const data = daily
    .filter((d) => d.orders > 0)
    .map((d) => ({ label: shortDay(d.date), Receita: d.revenue, Pedidos: d.orders }));
  if (data.length === 0) return <Empty>Nenhuma compra atribuída no período.</Empty>;
  const max = Math.max(...data.map((d) => d.Receita));

  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className={GRID} vertical={false} />
        <XAxis dataKey="label" tick={chartAxisTick} tickLine={false} axisLine={false} />
        <YAxis
          tick={chartAxisTick}
          tickLine={false}
          axisLine={false}
          width={yAxisWidth(max, axisMoney)}
          tickFormatter={axisMoney}
        />
        <Tooltip
          formatter={(v: number, name: string) => [name === 'Receita' ? brl(v) : decimal(v), name]}
          cursor={chartLineCursor}
          {...chartTooltipProps}
        />
        <Area
          type="monotone"
          dataKey="Receita"
          stroke="hsl(var(--primary))"
          fill="hsl(var(--primary))"
          fillOpacity={0.15}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Quantos dias depois do disparo as compras acontecem. */
export function PurchaseLagChart({ lag }: { lag: CampaignReport['purchaseLag'] }) {
  if (lag.every((l) => l.orders === 0)) return <Empty>Nenhuma compra atribuída no período.</Empty>;
  const data = lag.map((l) => ({
    label: l.day === 0 ? 'No dia' : `${l.day}d`,
    Pedidos: l.orders,
    Clientes: l.buyers,
  }));
  const max = Math.max(...data.map((d) => Math.max(d.Pedidos, d.Clientes)));

  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className={GRID} vertical={false} />
        <XAxis dataKey="label" tick={chartAxisTick} tickLine={false} axisLine={false} />
        <YAxis
          tick={chartAxisTick}
          tickLine={false}
          axisLine={false}
          width={yAxisWidth(max, axisCount)}
          tickFormatter={axisCount}
          allowDecimals={false}
        />
        <Tooltip formatter={(v: number) => decimal(v)} cursor={chartBarCursor} {...chartTooltipProps} />
        <Legend wrapperStyle={chartLegendStyle} />
        <Bar dataKey="Pedidos" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
        <Bar dataKey="Clientes" fill="hsl(var(--muted-foreground))" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Funil por pessoa (cada uma conta uma vez no período): público → receberam → compraram. */
export function FunnelBars({ totals }: { totals: CampaignReport['totals'] }) {
  const steps = [
    { label: 'Público dos disparos', value: totals.audience, hint: 'pessoas' },
    { label: 'Receberam', value: totals.recipients, hint: `${decimal(totals.reachRate, 1)}% do público` },
    { label: 'Clientes que compraram', value: totals.buyers, hint: `${decimal(totals.conversionRate, 1)}% de quem recebeu` },
  ];
  const max = Math.max(1, steps[0].value);

  return (
    <div className="space-y-4" style={{ minHeight: HEIGHT }}>
      {steps.map((step, i) => (
        <div key={step.label} className="space-y-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm">
            <span className="font-medium">{step.label}</span>
            <span className="whitespace-nowrap tabular-nums">
              <strong>{decimal(step.value)}</strong>
              <span className="ml-2 text-xs text-muted-foreground">{step.hint}</span>
            </span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${Math.max(step.value > 0 ? 2 : 0, (step.value / max) * 100)}%`, opacity: 1 - i * 0.25 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
